import { memo, useCallback } from "react";
import { useSelector } from "@tanstack/react-store";
import type { RowData } from "@tanstack/react-table";
import { styleWithVars } from "@lib/cssVars";
import { CellControlsTabbableContext } from "../cells/cellControls";
import { DraftRowCells } from "../cells/DraftRowInput";
import { getIsSummaryRow } from "../cells/summaryRow";
import type { DataGridCell, DataGridPin } from "../columns/types";
import { isInRangeSpan } from "../features/range/rangeKey";
import { getRovingTabIndex, isListNavigableColumn } from "../focus/navigation";
import { resolveColumnMeta } from "../columns/columnKinds";
import { useConstant } from "@hooks/useConstant";
import { Cell } from "./Cell";
import { RangeOutline } from "./RangeOutline";
import { getCellEditState } from "./cellEditState";
import { areRowPropsEqual, type RowProps } from "./rowProps";
import { getLiveRow, useRowState } from "./useRowState";
import styles from "./Grid.module.scss";

/**
 * One body row. Subscribes only to its own focus, selection, editor, and
 * highlight slices (`useRowState`), so focusing a cell re-renders just the
 * rows it touches; its `RangeOutline` follows the range on its own. In row
 * mode the focused row's cell controls join the Tab order.
 */
export const RowComponent = <TData extends RowData>({
    row: renderedRow,
    start,
    ariaRowIndex,
    virtualIndex,
    measureRef,
    isExpanded,
    isGroupEnd,
    layout,
    controller,
    atoms,
    hasCellNavigation,
    hasListEditing,
    hasFillHandle,
    hasRowSelection,
    isTree,
    hasParentSummaries,
    editContext,
    listEditContext,
    rowKinds,
    tableMeta,
    listEditing,
}: Readonly<RowProps<TData>>) => {
    const row = getLiveRow(renderedRow);
    const rowId = renderedRow.id;
    const {
        focusedColumnId,
        rangeSpan,
        rangeEdges,
        hasRangeAction,
        isListCellFocused,
        selection,
        isActive,
        editor,
        highlightKey,
    } = useRowState({
        row,
        atoms,
        getActiveKey: rowKinds.getActiveKey,
    });
    const isSelected = selection === "all";
    const rowStateKey = String(isExpanded);
    const isFocused = focusedColumnId !== null;
    const isRowFocusTarget = !hasCellNavigation;
    const hasCellRing = hasCellNavigation || isListCellFocused;
    const edges = hasCellRing ? rangeEdges.split(" ") : [];
    const isRangeTop = edges.includes("top");
    const hasRangeCorner =
        (hasFillHandle && editor === null && edges.includes("bottom")) ||
        (hasRangeAction && isRangeTop);
    const activeCell =
        hasCellRing && focusedColumnId !== null
            ? row.getAllCellsByColumnId()[focusedColumnId]
            : undefined;
    const activeCellMeta =
        focusedColumnId === null
            ? undefined
            : layout.metaById.get(focusedColumnId);
    const activeEditState =
        activeCell && activeCellMeta
            ? getCellEditState({
                  cell: activeCell,
                  meta: activeCellMeta,
                  highlightKey,
                  editor,
                  editContext,
              })
            : undefined;
    const isActiveCellInvalid = activeEditState?.invalidMessage !== undefined;
    const isEntering = useConstant(
        () => row.depth > 0 && controller.isJustExpanded(),
    );
    const isNew = useConstant(() => controller.rowMotion.isNewRow(rowId));
    const isDragSource = useSelector(
        controller.dragSource,
        (source) => source?.kind === "row" && source.id === rowId,
    );

    const rowRef = useCallback(
        (element: HTMLDivElement | null) => {
            measureRef?.(element);
            if (isRowFocusTarget) {
                controller.registerRow(rowId, element);
            }
            return () => {
                measureRef?.(null);
                if (isRowFocusTarget) {
                    controller.registerRow(rowId, null);
                }
            };
        },
        [controller, isRowFocusTarget, measureRef, rowId],
    );

    const isSummaryRow = getIsSummaryRow({
        hasParentSummaries,
        row,
        isParentRow: rowKinds.isParentRow,
    });

    const renderCell = (
        cell: DataGridCell<TData>,
        pin: DataGridPin | false,
    ) => {
        const meta =
            layout.metaById.get(cell.column.id) ??
            resolveColumnMeta(cell.column.columnDef, cell.column.id);
        const isCellFocusTarget =
            hasCellNavigation ||
            (hasListEditing && isListNavigableColumn(cell.column.columnDef));
        const ariaColIndex = layout.colIndexById.get(cell.column.id) ?? 0;
        const isActiveCell = hasCellRing && focusedColumnId === cell.column.id;
        const editState =
            (isActiveCell && activeEditState) ||
            getCellEditState({
                cell,
                meta,
                highlightKey,
                editor,
                editContext,
            });
        return (
            <Cell
                key={cell.id}
                cell={cell}
                meta={meta}
                pin={pin}
                pinEdge={layout.pinEdgeById.get(cell.column.id)}
                ariaColIndex={ariaColIndex}
                isInRange={
                    hasCellNavigation
                        ? isInRangeSpan(rangeSpan, ariaColIndex - 1)
                        : undefined
                }
                controller={isCellFocusTarget ? controller : null}
                isActive={isActiveCell}
                rowStateKey={rowStateKey}
                variant={layout.variantById.get(cell.column.id)}
                isSummaryRow={isSummaryRow}
                tableMeta={tableMeta}
                listEditContext={listEditContext}
                listEditing={listEditing}
                activeEditor={
                    editor?.columnId === cell.column.id ? editor : null
                }
                {...editState}
            />
        );
    };

    const startCells = row
        .getStartVisibleCells()
        .map((cell) => renderCell(cell, "start"));
    const centerCells = row
        .getCenterVisibleCells()
        .map((cell) => renderCell(cell, false));
    const endCells = row
        .getEndVisibleCells()
        .map((cell) => renderCell(cell, "end"));

    const renderRangeOutline = (side: "lead" | "trail") => (
        <RangeOutline
            row={row}
            atoms={atoms}
            side={side}
            layout={layout}
            hasHandle={hasFillHandle}
            isActiveCellInvalid={isActiveCellInvalid}
        />
    );

    const cells = (
        <>
            {hasCellRing ? renderRangeOutline("lead") : null}
            {startCells}
            {centerCells}
            <div className={styles.filler} aria-hidden />
            {endCells}
            {hasCellRing && layout.columnIdsByPart.end.length > 0
                ? renderRangeOutline("trail")
                : null}
        </>
    );
    const { Provider, draft } = rowKinds;
    const isDraft = rowKinds.isDraft(row.original);
    const providedCells = Provider ? (
        <Provider row={row.original}>{cells}</Provider>
    ) : (
        cells
    );

    return (
        <div
            {...rowKinds.getAttributes(row.original)}
            ref={rowRef}
            role="row"
            aria-rowindex={ariaRowIndex}
            data-index={virtualIndex}
            aria-selected={
                hasRowSelection && selection !== "some" ? isSelected : undefined
            }
            aria-expanded={row.getCanExpand() ? isExpanded : undefined}
            aria-level={isTree ? row.depth + 1 : undefined}
            tabIndex={getRovingTabIndex({
                isFocusTarget: isRowFocusTarget,
                isFocused: isFocused && !isListCellFocused,
            })}
            className={styles.row}
            style={styleWithVars({
                transform: `translateY(${start}px)`,
                "--dg-depth": row.depth,
            })}
            data-row-id={rowId}
            data-focused={(isRowFocusTarget && isFocused) || undefined}
            data-selected={isSelected || undefined}
            data-active={isActive || undefined}
            data-range-top={isRangeTop || undefined}
            data-range-corner={hasRangeCorner || undefined}
            data-group={row.getIsGrouped() || undefined}
            data-group-end={isGroupEnd || undefined}
            data-sub-row={row.depth > 0 || undefined}
            data-entering={isEntering || undefined}
            data-new={isNew || undefined}
            data-drag-source={isDragSource || undefined}
            data-measured={measureRef ? "" : undefined}
            data-draft={isDraft ? "" : undefined}
        >
            <CellControlsTabbableContext.Provider
                value={isRowFocusTarget && isFocused}
            >
                {isDraft && draft ? (
                    <DraftRowCells row={row.original} options={draft}>
                        {providedCells}
                    </DraftRowCells>
                ) : (
                    providedCells
                )}
            </CellControlsTabbableContext.Provider>
        </div>
    );
};

/**
 * Memoized row bound to one row type. TanStack row types are invariant in
 * TData, so each Body creates its own instance once instead of sharing a
 * module-level `memo(RowComponent)`.
 */
export const createMemoRow = <TData extends RowData>() =>
    memo(RowComponent<TData>, areRowPropsEqual<TData>);
